import type { RegistrationData } from '@/types'
import type { AxiosError } from 'axios'
import { useState } from 'react'
import authService from '../services/authService.js'
import useBrandUserStore from '../store/brandUserStore.ts'
import { logError } from '../utils/sentry'

interface RegistrationErrorData {
  data?: {
    email?: string[]
    [key: string]: unknown
  }
}

function isRegistrationErrorData(obj: unknown): obj is RegistrationErrorData {
  if (typeof obj !== 'object' || obj === null || !('data' in obj)) return false
  const data = (obj as { data?: unknown }).data
  return typeof data === 'object' && data !== null
}

export const useRegistrationFlow = () => {
  const [currentStep, setCurrentStep] = useState(1)
  const { registrationStepOne, registrationStepTwo } = useBrandUserStore()
  const nextStep = async (data: RegistrationData | Record<string, string>) => {
    try {
      switch (currentStep) {
        case 1: {
          try {
            const result = await registrationStepOne(data as RegistrationData)

            return !!result?.userId
          } catch (error) {
            const axiosError = error as AxiosError

            const responseData = axiosError.response?.data
            if (
              isRegistrationErrorData(responseData) &&
              Array.isArray(responseData.data?.email) &&
              responseData.data.email.some((msg) => msg.includes('already exists'))
            ) {
              throw {
                ...axiosError,
                fieldErrors: {
                  email: responseData.data.email,
                },
              }
            }

            console.error('Registration step one error:', error)
            throw error
          }
        }
        case 2: {
          const stepTwoResult = await registrationStepTwo(false)
          return !!stepTwoResult
        }

        default:
          return false
      }
    } catch (error) {
      const axiosError = error as AxiosError
      console.error('Full registration error:', {
        error,
        response: axiosError.response,
        responseData: axiosError.response?.data,
        message: axiosError.message,
      })

      logError('Unhandled registration error:', { error, step: currentStep })
      throw error
    }
  }

  const prevStep = () => {
    setCurrentStep((prev) => Math.max(1, prev - 1))
  }

  const resetRegistration = () => {
    setCurrentStep(1)
    useBrandUserStore.getState().clearErrors()
  }

  const saveAndExit = async () => {
    try {
      await registrationStepTwo(true)
      await authService.logout()
      window.location.href = '/'
    } catch (error) {
      logError('Save and exit error:', { error })
    }
  }

  return {
    currentStep,
    nextStep,
    prevStep,
    resetRegistration,
    saveAndExit,
  }
}
